import {
  useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { AppContext } from '../../providers/authProvider';
import api from '../../api/req';
import { readFile } from '../../api/utils';
import backendUri from '../../const/urls';

const FILES_URL = '/api/files';

function fixFiles(files) {
  return files.map(({ data, ...rest }) => ({
    data: `${backendUri}${data}`,
    ...rest,
  }));
}
// eslint-disable-next-line import/prefer-default-export
export const useFiles = ({ model, objectId }) => {
  const [options, setOptions] = useState({});
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [activeRef, setActiveRef] = useState(null);
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [descModalOpened, setDescModalOpened] = useState(false);
  const [currentDescription, setCurrentDescription] = useState(false);
  const menuOpened = !!activeRef;
  const activeFileUrl = useRef('');

  const { auth } = useContext(AppContext);
  const loadOptions = useCallback(
    async () => {
      const r = await api.options(`${FILES_URL}/${model}/${objectId}/`, auth);
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      return r.json();
    },
    [auth, model, objectId],
  );

  const load = useCallback(
    async () => {
      const r = await api.get(`${FILES_URL}/${model}/${objectId}/`, auth);
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      return r.json();
    },
    [auth, model, objectId],
  );

  const saveDescription = useCallback(
    async (description, fileId) => {
      const data = { description };
      const r = await api.patch(`${FILES_URL}/${model}/${objectId}/${fileId}/`, auth, data);
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      return r.json();
    },
    [auth, model, objectId],
  );

  const deleteFile = useCallback(
    async (fileId) => {
      const r = await api.delete(`${FILES_URL}/${model}/${objectId}/${fileId}/`, auth);
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      return true;
    },
    [auth, model, objectId],
  );

  useEffect(
    () => {
      if (descModalOpened) {
        setCurrentDescription(
          files.filter((f) => f.data === activeFileUrl.current).reduce((R, r) => r.description || '', ''),
        );
      }
    },
    [descModalOpened, files],
  );
  useEffect(
    () => {
      setLoading(true);
      setErr(null);
      loadOptions()
        .then((d) => setOptions(d))
        .catch((e) => setErr(e.message))
        .finally(() => setLoading(false));
    },
    [loadOptions],
  );

  useEffect(
    () => {
      if (options.actions) {
        setLoading(true);
        setErr(null);
        load()
          .then((d) => setFiles(fixFiles(d)))
          .catch((e) => setErr(e.message))
          .finally(() => setLoading(false));
      }
    },
    [load, options.actions],
  );

  const saveFile = useCallback(
    async (file) => {
      const content = await readFile(file);
      const data = {
        name: file.name,
        content: content.result,
      };
      const r = await api.post(`${FILES_URL}/${model}/${objectId}/`, auth, data);
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      return r.json();
    },
    [auth, model, objectId],
  );

  const onDragOver = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'link';
    },
    [],
  );
  const addFiles = useCallback(
    async (flz) => {
      const result = await Promise.allSettled([...flz].map((f) => saveFile(f)));
      const errs = result.filter((r) => r.status === 'rejected');
      if (errs.length) {
        throw new Error(
          errs.reduce((R, r) => `${R}${r.reason.message} `, ''),
        );
      }
      return load();
    },
    [load, saveFile],
  );

  const onAddFiles = useCallback(
    (flz) => {
      setLoading(true);
      setErr(false);
      addFiles(flz)
        .then((d) => setFiles(fixFiles(d)))
        .catch((e) => setErr(e.message))
        .finally(() => setLoading(false));
    },
    [addFiles],
  );

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      onAddFiles(e.dataTransfer.files);
    },
    [onAddFiles],
  );

  const onFileInputChange = useCallback(
    (e) => {
      onAddFiles(e.target.files);
    },
    [onAddFiles],
  );

  const onOpenMenu = useCallback(
    (ref, url) => {
      activeFileUrl.current = url;
      setActiveRef(ref.current);
    },
    [],
  );

  const onCloseMenu = useCallback(
    () => setActiveRef(null),
    [],
  );

  const onCloseDescModal = useCallback(
    () => setDescModalOpened(false),
    [],
  );

  const onChangeDescription = useCallback(
    (e, v) => setCurrentDescription(v),
    [],
  );

  const onSaveDescription = useCallback(
    () => {
      setDescModalOpened(false);
      const fileId = files
        .filter((f) => f.data === activeFileUrl.current)
        .reduce((R, f) => f.id, null);
      if (fileId) {
        setLoading(true);
        setErr(false);
        saveDescription(currentDescription, fileId)
          .then((partData) => setFiles(
            (ff) => ff.map((f) => (f.id === fileId ? { ...f, ...partData } : f)),
          ))
          .catch((e) => setErr(e.message))
          .finally(() => setLoading(false));
      }
    },
    [currentDescription, files, saveDescription],
  );

  const onCloseDeleteModal = useCallback(
    () => setDeleteModalOpened(false),
    [],
  );

  const onDeleteFile = useCallback(
    () => {
      setDeleteModalOpened(false);
      const fileId = files
        .filter((f) => f.data === activeFileUrl.current)
        .reduce((R, f) => f.id, null);
      if (fileId) {
        setLoading(true);
        setErr(false);
        deleteFile(fileId)
          .then(() => load())
          .then((d) => setFiles(fixFiles(d)))
          .catch((e) => setErr(e.message))
          .finally(() => setLoading(false));
      }
    },
    [deleteFile, files, load],
  );

  const onDownloadFile = useCallback(
    async () => {
      const url = activeFileUrl.current.replace(process.env.REACT_APP_API_SERVER, '');
      const r = await api.get(url, auth);
      if (r.ok) {
        const imageUrl = window.URL.createObjectURL(await r.blob());
        const tag = document.createElement('a');
        tag.href = imageUrl;
        tag.download = `${model}${objectId}`;
        document.body.appendChild(tag);
        tag.click();
        document.body.removeChild(tag);
      }
    },
    [auth, model, objectId],
  );

  const onMenuItemsClick = useMemo(
    () => ({
      onDescChange: () => {
        setDescModalOpened(true);
        setActiveRef(null);
      },
      onDelete: () => {
        setDeleteModalOpened(true);
        setActiveRef(null);
      },
      onDownload: () => {
        onDownloadFile();
        setActiveRef(null);
      },
    }),
    [onDownloadFile],
  );

  return {
    files,
    loading,
    err,
    activeRef,
    menuOpened,
    onDragOver,
    onDrop,
    onFileInputChange,
    onCloseMenu,
    onOpenMenu,
    onMenuItemsClick,
    descModalOpened,
    onCloseDescModal,
    currentDescription,
    onChangeDescription,
    onSaveDescription,
    deleteModalOpened,
    onCloseDeleteModal,
    onDeleteFile,
    onDownloadFile,
  };
};
